import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import {
  InstitutionBranding,
  cachedBranding,
  localBranding,
  resolveBranding,
} from '../services/institutions-service';

interface Props {
  /** Institution name, or the account name when no institution is recorded. */
  name?: string | null;
  /** Edge length in pixels. 24 in dense rows, 30-32 in lists, 40 on detail. */
  size?: number;
  /**
   * Skip the network entirely and draw from the name alone. For lists that
   * already primed the cache, or anywhere a request would be wasteful.
   */
  localOnly?: boolean;
  /** Accessible label. Defaults to the resolved institution name. */
  title?: string;
}

/**
 * The mark for a financial institution.
 *
 * Two states:
 *
 *   1. We hold logo bytes for this institution — draw them. They came either
 *      from Plaid or, far more often, from the institution's own website via
 *      the server-side sync in migration 053.
 *   2. Everything else — draw an initials monogram on a deterministic colour
 *      from the app's own series palette.
 *
 * The monogram is the design, not a placeholder: a meaningful minority of
 * institutions publish nothing usable, and hand-entered accounts that match no
 * institution at all are common. It uses the same hairline-and-tint vocabulary
 * as the rest of the app (no shadow, no gradient, no coloured pill) so a list
 * of eight accounts reads as one system whether or not any of them resolved.
 *
 * PRIVACY: every logo is INLINED as a data: URI from bytes our own server
 * cached. The browser must never request a logo from a third party — not
 * Clearbit, not Google's favicon service, not the bank itself — because each
 * such request would tell that party exactly which banks this user holds
 * accounts at. That is a leak, not a detail, and inlining is what forecloses
 * it structurally rather than by policy.
 *
 * TRADEMARK POSTURE: no bank asset is bundled with the app. Bytes arrive at
 * runtime from the rights holder's own server and are used to label the user's
 * own account at that institution, which is nominative use.
 */
export const InstitutionMark: React.FC<Props> = ({
  name,
  size = 30,
  localOnly = false,
  title,
}) => {
  const label = (name ?? '').trim();

  // Start from whatever is already known so the mark is drawn on the first
  // paint. A square that fills in later is a visible flicker down a list.
  const [branding, setBranding] = useState<InstitutionBranding>(
    () => cachedBranding(label) ?? localBranding(label),
  );

  useEffect(() => {
    const known = cachedBranding(label);
    if (known) {
      setBranding(known);
      return;
    }
    setBranding(localBranding(label));
    if (localOnly || !label) return;

    let alive = true;
    resolveBranding(label).then((b) => {
      // Guard against the name changing while the batch was in flight.
      if (alive) setBranding(b);
    });
    return () => {
      alive = false;
    };
  }, [label, localOnly]);

  const accent = `var(--series-${branding.color_index})`;
  const radius = Math.max(4, Math.round(size * 0.22));

  const frame = {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: `${radius}px`,
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
  } as const;

  if (branding.logo) {
    return (
      <Box
        sx={{
          ...frame,
          // Banks ship logos on white. A hairline plus a neutral plate keeps
          // them from glowing as bright squares in dark mode.
          background: 'var(--surface-solid-2)',
          border: '1px solid var(--border-soft)',
        }}
      >
        <Box
          component="img"
          // Server-sniffed type, defaulting to PNG for rows stored before the
          // column existed (every Plaid logo is a PNG).
          src={`data:${branding.logo_content_type || 'image/png'};base64,${branding.logo}`}
          alt={title ?? branding.name}
          sx={{ width: '82%', height: '82%', objectFit: 'contain', display: 'block' }}
          // If the bytes fail to decode, fall back to the monogram rather than
          // leaving the browser's broken-image glyph in a financial summary.
          onError={() => setBranding({ ...branding, logo: undefined })}
        />
      </Box>
    );
  }

  return (
    <Box
      role="img"
      aria-label={title ?? (branding.name || 'Unknown institution')}
      sx={{
        ...frame,
        // color-mix keeps one colour token driving fill, edge and text, so the
        // monogram stays legible in both themes without a second palette.
        background: `color-mix(in srgb, ${accent} 14%, transparent)`,
        border: `1px solid color-mix(in srgb, ${accent} 32%, transparent)`,
        color: accent,
        fontWeight: 700,
        // Three-character acronyms (RBC, BMO) must fit the same square as two.
        fontSize: `${Math.round(size * (branding.initials.length > 2 ? 0.32 : 0.4))}px`,
        letterSpacing: '0.01em',
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {branding.initials}
    </Box>
  );
};

/**
 * A mark followed by its name, ellipsised.
 *
 * Institution names run long ("Fédération des caisses Desjardins du Québec")
 * and wrapping them ruins the fixed-height rows the account lists depend on,
 * so the text truncates rather than reflowing the row.
 */
export const InstitutionLabel: React.FC<Props & { fallback?: string }> = ({
  name,
  size = 20,
  localOnly,
  fallback = '—',
}) => {
  const label = (name ?? '').trim();
  if (!label) {
    return (
      <Box component="span" sx={{ color: 'var(--text-faint)' }}>
        {fallback}
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <InstitutionMark name={label} size={size} localOnly={localOnly} />
      <Box
        component="span"
        sx={{
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Box>
    </Box>
  );
};
